import { Body, Box, Container, Heading, Icon, Divider } from '@hover/blueprint';
import { iMeh } from '@hover/icons';
import { graphql, navigate } from 'gatsby';
import React, { useEffect, useReducer, useState } from 'react';
import { WindowLocation } from '@reach/router';
import {
  BlogCards,
  BlogCategoriesTabs,
  BlogHeroPost,
} from '../../../v2/components/blog';
import { Layout } from '../../../v2/components/common';
import { SearchBar } from '../../../v2/components/common/SearchBar';
import { PaginationGroup } from '../../../v2/components/ui';
import BLOG_CATEGORIES, { BLOG_INDEX_PAGE_LIMIT } from '../../../v2/constants';
import { IBlogPosts } from '../../../v2/interfaces/IBlogPosts';
import { pageNumberController } from '../../../v2/utils';
import blogPostsQuery from '../../../v2/utils/queries';
import blogReducer from '../../../v2/utils/reducers';

interface BlogPostsQuery {
  data: {
    blogPostHero: IBlogPosts;
  };
  location: WindowLocation;
}

const Blog = ({ data, location }: BlogPostsQuery) => {
  const [activeCategory, setActiveCategory] = useState(BLOG_CATEGORIES[0]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [keyword, setKeyword] = useState<string>('');
  const firstBlogPost = data.blogPostHero.edges[0].node;
  const isSearchActive = activeCategory.key === 'search';

  const [{ data: categoriesData, pageData, isLoading }, dispatch] = useReducer(
    blogReducer,
    {
      isLoading: false,
      data: {},
      pageData: {},
    },
  );

  const isNotInitial = Boolean(pageData[activeCategory.value]?.limit);

  const {
    totalPagesNumber,
    currentPageNumber,
    nextPageNumber,
    prevPageNumber,
  } = pageNumberController({
    isNotInitial,
    limit: BLOG_INDEX_PAGE_LIMIT,
    total: pageData[activeCategory.value]?.total,
    currentPage: pageNumber,
  });

  function checkRequestCondition() {
    if (!isNotInitial) return true;
    if (
      categoriesData[activeCategory.value] &&
      categoriesData[activeCategory.value][pageNumber] &&
      categoriesData[activeCategory.value][pageNumber]?.length
    )
      return false;
    return pageNumber > 0 && pageNumber <= totalPagesNumber!;
  }

  const canShowBlogCards =
    categoriesData &&
    activeCategory &&
    categoriesData[activeCategory.value] &&
    categoriesData[activeCategory.value][pageNumber]?.length;

  const handleActiveCategory = (item: {
    value: string;
    key: string;
    color: string;
    fromTips?: boolean;
  }) => {
    setActiveCategory(item);
    setPageNumber(1);
    if (location?.search.length && !item.fromTips) {
      const params: URLSearchParams = new URLSearchParams(location?.search);
      // @ts-ignore
      const fromQuery = params?.get('from').toLowerCase();
      if (fromQuery === 'tips') {
        void navigate(`${process.env.GATSBY_V2_PREFIX}/blog`);
      }
    }
  };

  const resetCategories = () => {
    setKeyword('');
    handleActiveCategory(BLOG_CATEGORIES.find(item => item.key === 'all')!);
  };

  const handleSubmitSearch = ({
    keyword: inputKeyword,
  }: {
    keyword: string;
  }) => {
    if (inputKeyword) {
      dispatch({ type: 'reset-search' });
      setKeyword(inputKeyword);
      handleActiveCategory(
        BLOG_CATEGORIES.find(item => item.key === 'search')!,
      );
    } else {
      resetCategories();
    }
  };

  useEffect(() => {
    if (checkRequestCondition()) {
      blogPostsQuery({
        pageNumber,
        activeCategory,
        categoriesData,
        pageData,
        dispatch,
        keyword,
      });
    }
  }, [activeCategory, pageNumber, keyword]);

  useEffect(() => {
    if (location?.search?.length) {
      const params: URLSearchParams = new URLSearchParams(location.search);
      // @ts-ignore
      const fromQuery = params.get('from').toLowerCase();
      if (fromQuery === 'tips') {
        handleActiveCategory({
          value: 'Exterior Contractors',
          key: 'exterior-contractors',
          color: 'brandOrange',
          fromTips: true,
        });
      }
    }
  }, [location]);

  let canShowCategories = true;

  if (activeCategory.value === 'Search') {
    if (!isLoading) {
      canShowCategories =
        !!categoriesData[activeCategory.value] &&
        !!categoriesData[activeCategory.value][pageNumber]?.length;
    }
  }

  return (
    <Layout
      title="Blog"
      description="We share the latest industry trends, best practices, HOVER product and company updates with exterior contractors and insurance carriers here."
    >
      <Container maxW="1248px" px={500}>
        <SearchBar
          handleSubmit={handleSubmitSearch}
          hideBottomDivider={isSearchActive}
        />
        {isSearchActive ? null : <BlogHeroPost my={700} post={firstBlogPost} />}
        {canShowCategories ? (
          <BlogCategoriesTabs
            activeCategory={activeCategory}
            handleClick={handleActiveCategory}
          />
        ) : (
          <Divider color="neutral.300" />
        )}
        <Box pb="90px" display="flex" flexDir="column">
          {!isSearchActive && activeCategory !== BLOG_CATEGORIES[0] ? (
            <Box my={800} display={{ base: 'none', tablet: 'flex' }}>
              <Heading
                size={600}
                textTransform="capitalize"
                marginBottom={0}
                fontWeight={400}
              >
                {activeCategory.value}
              </Heading>
            </Box>
          ) : (
            <Box mb={{ base: 0, tablet: 700 }} />
          )}
          {isSearchActive && canShowBlogCards ? (
            <Box mb={700}>
              <Body size={500} fontWeight={300} display="flex">
                Search results for :&nbsp;
                <Body fontWeight={600}>“{keyword}”</Body>
              </Body>
            </Box>
          ) : null}
          {canShowBlogCards ? (
            <>
              <BlogCards
                posts={categoriesData[activeCategory.value][pageNumber]}
                mb={{ base: '56px', tablet: '88px' }}
              />
              <PaginationGroup
                prevPageNumber={prevPageNumber}
                setPageNumber={setPageNumber}
                currentPageNumber={currentPageNumber}
                nextPageNumber={nextPageNumber}
                totalPagesNumber={totalPagesNumber}
                mb={400}
              />
            </>
          ) : null}
          {isLoading ? <Box pb="100vh" /> : null}
          {!canShowBlogCards && isSearchActive && !isLoading ? (
            <Box
              w="100%"
              display="flex"
              alignItems="center"
              flexDir="column"
              mt={{ base: 800, desktop: 0 }}
              minH="84vh"
            >
              <Icon
                icon={iMeh}
                color="warning.500"
                height={500}
                width={500}
                size="default"
                strokeWidth="1px!important"
              />
              <Body
                size={500}
                fontWeight={300}
                d="inline"
                mt={600}
                flexDir="row"
                textAlign="center"
              >
                Sorry, we couldnt’t find any search results for &nbsp;
                <Body d="inline" fontWeight={600}>
                  “{keyword}”.
                </Body>
              </Body>
              <Body
                size={500}
                fontWeight={300}
                display="inline"
                textAlign="center"
              >
                Please try again or return to the &nbsp;
                <Box
                  d="inline"
                  as="button"
                  fontWeight={600}
                  _hover={{
                    textDecoration: 'underline',
                    color: 'primary.800',
                  }}
                  onClick={() => {
                    resetCategories();
                  }}
                  color="primary.600"
                >
                  blog overview.
                </Box>
              </Body>
            </Box>
          ) : null}
        </Box>
      </Container>
    </Layout>
  );
};

export default Blog;

export const pageQuery = graphql`
  query NewBlogQuery($category: String) {
    blogPostHero: allContentfulBlogPost(
      filter: { categories: { eq: $category } }
      sort: { fields: [publishDate], order: DESC }
      limit: 1
    ) {
      edges {
        node {
          title
          slug
          categories
          publishDate(formatString: "MM/DD/YYYY")
          heroImage {
            fluid(maxHeight: 585, resizingBehavior: SCALE) {
              ...GatsbyContentfulFluid
            }
          }
          description {
            description
          }
        }
      }
    }
  }
`;
